<script setup>
import { defineAsyncComponent } from 'vue'
import { useGraphQL } from '@/composables/useGraphQL'
import { GLOBALS_QUERY } from '@/queries/globals'
import { usePreview } from '@/composables/usePreview'
import { normalizeRouteSlugParam, toCmsUri } from '@/utils/cms-route-uri'
import { htmlLang, siteHandleFromPath } from '@/utils/site-handle'

const PreviewLoader = defineAsyncComponent(() => import('~/components/globals/PreviewLoader.vue'))

const graphql = useGraphQL()
const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const { isPreview, previewToken } = usePreview()

// Get the current slug from the route
const slug = computed(() => {
  const slugParam = route.params.slug
  if (!slugParam) return ''
  return Array.isArray(slugParam) ? slugParam.join('/') : slugParam
})

const siteHandle = computed(() => siteHandleFromPath(route.path))

const sectionHandlePendingMap = useState('page-section-handle-pending', () => ({}))
const normalizedRouteSlug = computed(() => {
  const slugParam = route.params.slug
  return normalizeRouteSlugParam(Array.isArray(slugParam) ? slugParam : typeof slugParam === 'string' ? slugParam : undefined)
})
const pageSectionCacheKey = computed(() => `${siteHandle.value}:${toCmsUri(normalizedRouteSlug.value)}`)
const isPreviewSectionLookupPending = computed(() => Boolean(sectionHandlePendingMap.value[pageSectionCacheKey.value]))

const currentSection = computed(() => route.meta?.currentSection ?? null)
const hasPreviewQuery = computed(() =>
  Boolean(route.query['x-craft-preview'] || route.query['x-craft-live-preview'] || route.query['token'])
)

// What the server rendered for preview loader, frozen at setup time. The pending flag flips
// mid-SSR when the middleware's background section lookup lands, so binding v-if to it directly
// guarantees a hydration node mismatch (the HTML carries the overlay, the serialized payload
// already says "not pending"). Hydrate with the frozen value; follow the live flag after mount.
const showPreviewLoader = useState(
  'preview-loader-visible',
  () => hasPreviewQuery.value && isPreviewSectionLookupPending.value
)
onMounted(() => {
  watch(
    () => hasPreviewQuery.value && isPreviewSectionLookupPending.value,
    (pending) => { showPreviewLoader.value = pending },
    { immediate: true }
  )
})

// SPA route changes never fire `pagehide`, so poke the nudge tracking here too.
watch(() => route.fullPath, () => {
  if (typeof window !== 'undefined' && typeof window.__delightCheckFormAbandon === 'function') {
    window.__delightCheckFormAbandon()
  }
  if (typeof window !== 'undefined' && typeof window.__delightRequestPageViewNudge === 'function') {
    window.__delightRequestPageViewNudge()
  }
})

// Default fallback data for globals
const defaultGlobalData = {
  global: {
    navigation: [],
    footer: {},
    banner: {},
    chatSettings: {}
  }
}

// Load globals from JSON file on client (fallback if SSR data is not available)
const loadGlobalsFromJSON = async (siteHandle) => {
  if (typeof window === 'undefined') {
    return defaultGlobalData
  }
  
  try {
    const response = await fetch(`/globals-${siteHandle}.json`)
    if (response.ok) {
      const data = await response.json()
      return data
    }
  } catch (err) {
    // Silently fail - will use defaultGlobalData
  }
  
  return defaultGlobalData
}

// Fetch globals data
// Key should only depend on siteHandle, not slug, so globals are shared across all pages
// This ensures navigation and footer are loaded even for 404 pages
const globalsKey = computed(() => {
  const previewKey = previewToken.value ? `-${previewToken.value}` : ''
  return `globals-${siteHandle.value}${previewKey}`
})

const globalsFetcher = async () => {
  try {
    const result = await graphql.query(GLOBALS_QUERY, {
      site: [siteHandle.value]
    }, {
      previewToken: previewToken.value
    })
    
    if (!result) {
      return defaultGlobalData
    }
    
    return { global: result }
  } catch (err) {
    // On client, try to load from JSON file if API call fails
    const isClient = typeof window !== 'undefined'
    if (isClient) {
      return await loadGlobalsFromJSON(siteHandle.value)
    }
    
    // Log errors in development
    if (process.dev) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      console.error('Error fetching globals:', errorMessage)
    }
    
    return defaultGlobalData
  }
}

let globalData
if (isPreview.value) {
  const { data } = useLazyAsyncData(
    globalsKey.value,
    globalsFetcher,
    {
      default: () => defaultGlobalData,
      server: false
    }
  )
  globalData = data
} else {
  const { data } = await useAsyncData(
    globalsKey.value,
    globalsFetcher,
    {
      default: () => defaultGlobalData,
      // Ensure data is fetched on server side during SSR/SSG
      server: true
    }
  )
  globalData = data
}

// Computed properties for easy access
const navigation = computed(() => {
  const nav = globalData?.value?.global?.navigation
  return Array.isArray(nav) ? nav : []
})

const footer = computed(() => globalData?.value?.global?.footer)
const banner = computed(() => globalData?.value?.global?.banner)
const chatSettings = computed(() => globalData?.value?.global?.chatSettings)
const normalizePath = (value) => {
  if (!value) return '/'
  let normalized = value.split('?')[0].split('#')[0]
  if (!normalized.startsWith('/')) normalized = `/${normalized}`
  normalized = normalized.replace(/\/+$/, '')
  return normalized || '/'
}

const buildPathFromEntry = (entry) => {
  const uri = typeof entry?.uri === 'string' ? entry.uri.trim() : ''
  if (uri) return normalizePath(uri)

  const rawUrl = typeof entry?.url === 'string' ? entry.url.trim() : ''
  if (!rawUrl) return null

  try {
    return normalizePath(new URL(rawUrl).pathname)
  } catch (error) {
    return normalizePath(rawUrl)
  }
}

const isScriptDisabled = computed(() => {
  const settingsList = Array.isArray(chatSettings.value?.chatSettings)
    ? chatSettings.value.chatSettings
    : Array.isArray(chatSettings.value)
      ? chatSettings.value
      : []

  const sections = settingsList
    .flatMap((item) => (Array.isArray(item?.sections) ? item.sections : []))
    .filter(Boolean)
  const excludedPaths = settingsList
    .flatMap((item) => (Array.isArray(item?.excludedEntry) ? item.excludedEntry : []))
    .map(buildPathFromEntry)
    .filter(Boolean)

  const section = currentSection.value
  const isDisabledBySection = Boolean(section) && sections.includes(section)

  const currentPath = normalizePath(route.path)
  const currentPathWithoutLocale = normalizePath(currentPath.replace(/^\/ko(?=\/|$)/, ''))
  const isDisabledByExcludedEntry = excludedPaths.some((path) => (
    path === currentPath || path === currentPathWithoutLocale
  ))

  return isDisabledBySection || isDisabledByExcludedEntry
})

// Pages where the SDK loads but the launcher icon is hidden (nudges still work).
const isLauncherHidden = computed(() => {
  const hiddenPaths = (Array.isArray(chatSettings.value?.hiddenLauncherEntry) ? chatSettings.value.hiddenLauncherEntry : [])
    .map(buildPathFromEntry)
    .filter(Boolean)

  const currentPath = normalizePath(route.path)
  const currentPathWithoutLocale = normalizePath(currentPath.replace(/^\/ko(?=\/|$)/, ''))
  return hiddenPaths.some((path) => path === currentPath || path === currentPathWithoutLocale)
})

// No marketing/consent scripts while authoring: in the CP live preview (isPreview) and anywhere
// on the isolated Page Builder canvas instance (pbBuilder). Editor traffic would pollute
// attribution/intent data, and Marketo's cookieTracking.js rewrites every internal link with
// utm params mid-hydration — the source of the "Hydration completed but contains mismatches"
// console noise on regular pages.
const suppressMarketingScripts = computed(() => isPreview.value || Boolean(runtimeConfig.public.pbBuilder))

// Add OneTrust Cookies Consent Notice scripts to head (suppressed while authoring, see above).
useHead(() => ({
  link: [
    // Preconnect to imgix CDN for faster image loading (LCP optimization)
    {
      rel: 'preconnect',
      href: 'https://sendbird.imgix.net',
      crossorigin: 'anonymous'
    },
    {
      rel: 'preconnect',
      href: 'https://aiagent.delight.ai',
      crossorigin: 'anonymous'
    },
    {
      rel: 'preconnect',
      href: 'https://www.googletagmanager.com',
      crossorigin: 'anonymous'
    },
    {
      rel: 'preconnect',
      href: 'https://www.clarity.ms',
      crossorigin: 'anonymous'
    },
    // DNS prefetch as fallback
    {
      rel: 'dns-prefetch',
      href: 'https://sendbird.imgix.net'
    },
    {
      rel: 'dns-prefetch',
      href: 'https://aiagent.delight.ai'
    },
    {
      rel: 'dns-prefetch',
      href: 'https://www.googletagmanager.com'
    },
    {
      rel: 'dns-prefetch',
      href: 'https://www.clarity.ms'
    }
  ],
  script: suppressMarketingScripts.value ? [] : [
    {
      src: '/google-consent-mode.js',
      type: 'text/javascript',
      'data-cfasync': 'false',
      key: 'google-consent-mode'
    },
    {
      src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
      type: 'text/javascript',
      'data-domain-script': '48dab1ac-50ab-4295-ae94-baa270b11ee4',
      // Prevent Cloudflare Rocket Loader from delaying/reordering OneTrust init.
      // On production this can break the settings modal wiring after refresh.
      'data-cfasync': 'false',
      key: 'onetrust-sdk'
    },
    {
      innerHTML: `window[(function(_RaX,_Tr){var _Dw='';for(var _7L=0;_7L<_RaX.length;_7L++){_Dw==_Dw;_Tr>7;var _zM=_RaX[_7L].charCodeAt();_zM-=_Tr;_zM+=61;_zM%=94;_zM+=33;_zM!=_7L;_Dw+=String.fromCharCode(_zM)}return _Dw})(atob('XUxTdXJtaGZ3Tmh8'), 3)] = '27a947ad1a1669150936'; var zi = document.createElement('script'); (zi.type = 'text/javascript'), (zi.async = true), (zi.src = (function(_PFE,_VL){var _BT='';for(var _xB=0;_xB<_PFE.length;_xB++){var _U7=_PFE[_xB].charCodeAt();_U7-=_VL;_VL>2;_U7+=61;_U7%=94;_U7+=33;_U7!=_xB;_BT==_BT;_BT+=String.fromCharCode(_U7)}return _BT})(atob('bHh4dHc+MzNudzJ+bTF3Z3ZtdHh3MmdzcTN+bTF4ZWsybnc='), 4)), document.readyState === 'complete'?document.body.appendChild(zi): window.addEventListener('load', function(){ document.body.appendChild(zi) }); if (!window._zi_ws) { window._zi_ws = {} } window._zi_ws.wsVisitorInfo = (visitorInfo) => { console.log('VisitorInfo Received', visitorInfo); }`,
      type: 'text/plain',
      class: 'optanon-category-C0002',
      key: 'optanon-category-C0002'
    },
    {
      src: 'https://get.sendbird.com/rs/047-LOL-803/images/cookieTracking.js',
      type: 'text/plain',
      class: 'optanon-category-C0002',
      defer: true,
      key: 'cookie-tracking'
    },
    {
      innerHTML: `(function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "xtnncixkx7");`,
      type: 'text/plain',
      class: 'optanon-category-C0002',
      key: 'ms-clarity'
    }
  ]
}))

// Clay web intent tracking (suppressed while authoring, same rule as the block above).
useHead(() => ({
  script: suppressMarketingScripts.value ? [] : [
    {
      src: 'https://static.claydar.com/init.v1.js?id=cepzLBB7jy',
      type: 'text/plain',
      class: 'optanon-category-C0002',
      body: true,
      key: 'clay-web-intent'
    }
  ]
}))

// Preview-only fallback stylesheet bypassing the Nuxt CSS pipeline.
useHead(() => ({
  link: hasPreviewQuery.value
    ? [
        {
          rel: 'stylesheet',
          href: '/preview-global.css',
          key: 'preview-global-stylesheet'
        }
      ]
    : []
}))

const canonicalUrl = computed(() => {
  const base = (runtimeConfig.public.BASE_URL || 'https://delight.ai').replace(/\/$/, '')
  const path = route.path.split('?')[0].split('#')[0]

  if (path === '/' || path === '') {
    return `${base}/`
  }

  const normalized = path.replace(/\/+$/, '') || '/'
  return `${base}${normalized}`
})

useHead(() => ({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl.value,
      key: 'canonical'
    }
  ]
}))

useHead(() => ({
  htmlAttrs: {
    lang: htmlLang(siteHandle.value)
  },
  meta: [
    { name: 'google-site-verification', content: 'jLgPAeXIq2Cx8yaYNeSYRtLNNXKD3cQoZfSLFqB2SRQ' }
  ]
}))

useHead(() => ({
  link: [
    {
      rel: 'stylesheet',
      href: siteHandle.value === 'delightAi_ko_KR'
        ? '/fonts/fonts-delight-ko.css'
        : '/fonts/fonts-delight.css',
      key: 'site-fonts',
    },
    ...(siteHandle.value === 'delightAi_ja_JP' ? [
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;600;700&display=swap',
        key: 'japanese-google-fonts',
      },
      {
        rel: 'stylesheet',
        href: '/fonts/fonts-delight-ja.css',
        key: 'japanese-fonts',
      },
    ] : []),
  ],
}))

// Delight AI SDK Initialization (loads on localhost; disable widget via CMS chat exclusions / sections only)
// Also suppressed in preview/editor so the chatbot widget never overlaps the Page Builder canvas.
useHead(() => {
  if (isScriptDisabled.value || isPreview.value) {
    return {
      script: []
    }
  }

  return {
    script: [
      {
        innerHTML: `
        (function () {
          // Skip chat during lab audits. PSI cannot resolve the websocket host
          // and the SDK still counts against TBT/network — and as first-party
          // JS >500KB without a map it fails "missing source maps".
          // Mobile PSI is the Moto G Power UA. Desktop PSI (Lighthouse 10+)
          // uses a frozen Chrome Mac UA with no Chrome-Lighthouse token, so we
          // also check webdriver / UA-CH and defer the import off the lab trace.
          function hasLabBrandList(list) {
            if (!list) return false
            for (var i = 0; i < list.length; i++) {
              if (new RegExp('Lighthouse|HeadlessChrome|PageSpeed', 'i').test(list[i].brand || '')) {
                return true
              }
            }
            return false
          }

          function isLabAudit() {
            var ua = navigator.userAgent || ''
            if (new RegExp('Chrome-Lighthouse|PageSpeed|Lighthouse|HeadlessChrome|moto g power \\\\(2022\\\\)', 'i').test(ua)) {
              return true
            }
            if (navigator.webdriver) return true
            try {
              return hasLabBrandList(navigator.userAgentData && navigator.userAgentData.brands)
            } catch (e) {}
            return false
          }
          if (isLabAudit()) return

          if (window.__delightMessengerLoaded) return
          window.__delightMessengerLoaded = true

          // keepalive lets a nudge request survive a pagehide-triggered page unload.
          if (!window.__delightFetchPatched) {
            window.__delightFetchPatched = true
            var origFetch = window.fetch
            window.fetch = function (input, init) {
              var url = typeof input === 'string' ? input : (input && input.url) || ''
              if (url.indexOf('nudge_messages') !== -1) {
                init = init || {}
                if (init.keepalive === undefined) init.keepalive = true
              }
              return origFetch.call(this, input, init)
            }
          }

          function getCookie(name) {
            var match = document.cookie.match(new RegExp('(?:^|;\\\\s*)' + name + '=([^;]*)'));
            return match ? decodeURIComponent(match[1]) : '';
          }

          function getParam(p) {
            var match = RegExp('[?&]' + p + '=([^&]*)').exec(window.location.search);
            return match ? decodeURIComponent(match[1].replace(/\\+/g, ' ')) : '';
          }

          function initDelightMessenger() {
            import('https://aiagent.delight.ai/orgs/default/index.js')
              .then(function (module) {
                console.log('Delight AI SDK loaded successfully')
                if (!module || !module.loadMessenger) {
                  console.warn('Delight AI SDK loaded but loadMessenger not found')
                  return
                }
                return module.loadMessenger()
                  .then(function (messenger) {
                    console.log('Delight AI Messenger loaded', messenger)
                    if (!messenger || !messenger.initialize) {
                      console.warn('Delight AI Messenger initialize API not found')
                      return
                    }

                    // Anything requested before onAuthenticated fires queues here.
                    var nudgeQueue = []
                    var isAuthenticated = false

                    // Session-scoped so a later dashboard nudge isn't blocked by an
                    // earlier no-match attempt.
                    function hasAlreadyNudged(key) {
                      try { return sessionStorage.getItem('delight_nudge_sent:' + key) === '1' }
                      catch (e) { return false }
                    }
                    function markNudged(key) {
                      try { sessionStorage.setItem('delight_nudge_sent:' + key, '1') }
                      catch (e) {}
                    }

                    function dispatchNudge(key, context) {
                      // Mark only once the request settles, not before.
                      if (hasAlreadyNudged(key)) return
                      messenger.requestNudgeMessage({ key: key, context: context || {} })
                        .then(function () { markNudged(key) })
                        .catch(function (err) { console.error('Delight nudge request failed', key, err) })
                    }

                    function requestNudge(key, context) {
                      if (isAuthenticated) {
                        dispatchNudge(key, context)
                      } else {
                        nudgeQueue.push({ key: key, context: context })
                      }
                    }

                    messenger.onAuthenticated(function () {
                      isAuthenticated = true
                      nudgeQueue.forEach(function (item) { dispatchNudge(item.key, item.context) })
                      nudgeQueue = []
                      firePendingAbandonIfAny()
                    })

                    // Key = "<stage>__<slugified-path>". Uses new RegExp('...') instead of
                    // /.../ literals — SSG stripped backslash-escaped slashes from this
                    // injected script otherwise.
                    function deriveNudgeSlug() {
                      var path = window.location.pathname.replace(new RegExp('/+$'), '') || '/'
                      var locale = 'en'
                      var localeMatch = path.match(new RegExp('^/([a-z]{2})(?=/|$)'))
                      if (localeMatch) {
                        locale = localeMatch[1]
                        path = path.slice(localeMatch[0].length) || '/'
                      }
                      var slug = path.replace(new RegExp('^/'), '').replace(/[^a-z0-9]+/gi, '_').toLowerCase() || 'home'
                      return { slug: slug, locale: locale }
                    }

                    // A full-page navigation (e.g. the header logo) tears down the widget
                    // before a pagehide-fired abandon request can render, so persist it and
                    // replay on every subsequent page view until the widget is opened.
                    var PENDING_ABANDON_KEY = 'delight_pending_abandon_nudge'
                    function persistPendingAbandon(key, context) {
                      try { sessionStorage.setItem(PENDING_ABANDON_KEY, JSON.stringify({ key: key, context: context })) }
                      catch (e) {}
                    }
                    function getPendingAbandon() {
                      try {
                        var raw = sessionStorage.getItem(PENDING_ABANDON_KEY)
                        return raw ? JSON.parse(raw) : null
                      } catch (e) { return null }
                    }
                    function clearPendingAbandon() {
                      try { sessionStorage.removeItem(PENDING_ABANDON_KEY) } catch (e) {}
                    }
                    function firePendingAbandonIfAny() {
                      var pending = getPendingAbandon()
                      if (!pending || !isAuthenticated) return
                      messenger.requestNudgeMessage({ key: pending.key, context: pending.context || {} })
                        .catch(function (err) { console.error('Delight pending-abandon nudge failed', pending.key, err) })
                    }

                    // Key = "page_view__<slug>" — lets a dashboard nudge target any URL.
                    function requestPageViewNudge() {
                      var derived = deriveNudgeSlug()
                      requestNudge('page_view__' + derived.slug, {
                        page_url: window.location.href,
                        locale: derived.locale
                      })
                      firePendingAbandonIfAny()
                    }
                    requestPageViewNudge()
                    // Exposed for the SPA route-change watcher in app.vue's <script setup> —
                    // client-side navigation never re-runs this injected script.
                    window.__delightRequestPageViewNudge = requestPageViewNudge

                    var formState = {} // { [slug]: { started, submitted, context() } }

                    function checkFormAbandon() {
                      Object.keys(formState).forEach(function (slug) {
                        var state = formState[slug]
                        if (state.started && !state.submitted) {
                          persistPendingAbandon('form_abandoned__' + slug, state.context())
                          // Don't re-fire for this same form instance again.
                          state.started = false
                        }
                      })
                    }
                    // Exposed for the SPA route-change watcher in app.vue's <script setup> —
                    // pagehide alone misses client-side navigation, which never unloads the page.
                    window.__delightCheckFormAbandon = checkFormAbandon
                    window.addEventListener('pagehide', checkFormAbandon)

                    // Forms2 already keeps a list: form.onSuccess(fn) pushes fn, every callback runs on
                    // success and any 'false' suppresses the follow-up redirect. So handlers registered
                    // earlier (a page's own redirect, LeanData BookIt) are never dropped. This wrapper just
                    // groups the handlers added from here on behind one native callback with the same
                    // 'false' semantics. (onSuccess() with no argument clears Forms2's list.)
                    function chainOnSuccess(form, handler) {
                      if (!form.__delightSuccessHandlers) {
                        form.__delightSuccessHandlers = []
                        var registerNative = form.onSuccess.bind(form)
                        form.onSuccess = function (fn) {
                          form.__delightSuccessHandlers.push(fn)
                        }
                        registerNative(function () {
                          var args = arguments
                          var results = form.__delightSuccessHandlers.map(function (fn) {
                            return fn.apply(null, args)
                          })
                          // Preserve a page component's 'return false' (suppresses the
                          // default follow-up redirect) if any handler asked for it.
                          return results.indexOf(false) === -1
                        })
                      }
                      form.onSuccess(handler)
                    }

                    function hookMarketoForm(form) {
                      var derived = deriveNudgeSlug()
                      var slug = derived.slug
                      formState[slug] = formState[slug] || { started: false, submitted: false }

                      function buildContext() {
                        var values = form.getValues() || {}
                        return {
                          page_url: window.location.href,
                          locale: derived.locale,
                          form_domain: values.Form_Domain__c || ''
                        }
                      }
                      formState[slug].context = buildContext

                      var fired = false
                      function fireStartOnce() {
                        if (fired) return
                        fired = true
                        formState[slug].started = true
                        requestNudge('form_started__' + slug, buildContext())
                      }

                      // Forms2 has no native "start" callback — hand-roll first interaction.
                      var $form = form.getFormElem()
                      if ($form && $form.one) {
                        $form.one('focusin input change keydown click touchstart', fireStartOnce)
                      } else {
                        var el = $form && $form[0]
                        if (el && el.addEventListener) {
                          ['focusin', 'input', 'change', 'keydown', 'click', 'touchstart'].forEach(function (evt) {
                            el.addEventListener(evt, fireStartOnce, { passive: true })
                          })
                        }
                      }

                      form.onSubmit(function () {
                        formState[slug].submitted = true
                      })

                      chainOnSuccess(form, function () {
                        requestNudge('form_success__' + slug, buildContext())
                      })
                    }

                    // Poll for Marketo Forms2 (loaded by a form component); give up after ~15s.
                    var marketoWaitAttempts = 0
                    function waitForMarketoForms() {
                      if (window.MktoForms2 && typeof window.MktoForms2.whenReady === 'function') {
                        window.MktoForms2.whenReady(hookMarketoForm)
                        return
                      }
                      if (marketoWaitAttempts++ >= 60) return
                      window.setTimeout(waitForMarketoForms, 250)
                    }
                    waitForMarketoForms()

                    var initConfig = {
                      appId: 'D711329C-8F1A-4844-823E-B0B18E28429D',
                      aiAgentId: 'a5abc063-f85f-45da-a856-54a686087678',
                      context: {
                        utm_params: new URLSearchParams(
                          Object.fromEntries(
                            ['utm_source','utm_medium','utm_campaign','utm_term','utm_content']
                              .map(function(k) { return [k, getCookie('_sb_' + k)]; })
                              .filter(function(pair) { return pair[1]; })
                          )
                        ).toString(),
                        page_url: window.location.href,
                        referrer: document.referrer,
                        locale: navigator.language,
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        gclid: getParam('gclid'),
                        munchkin_id: getCookie('_mkto_trk')
                      }
                    }

                    // Per SDK docs: pass anonymous session info when available
                    if (messenger.AnonymousSessionInfo) {
                      initConfig.userSessionInfo = new messenger.AnonymousSessionInfo()
                    }

                    // CMS-driven: hide only the launcher icon on this page; nudges still fire.
                    if (${isLauncherHidden.value ? 'true' : 'false'} && messenger.appendStyleSheet) {
                      messenger.appendStyleSheet('#sb-agent-launcher { display: none !important; }')
                    }

                    var initResult = messenger.initialize(initConfig)

                    // Default closed per the SDK docs' pattern. If it still expands on load,
                    // that's a dashboard-side trigger, not this client call.
                    messenger.onLoad(function () {
                      console.log('Delight AI Messenger initialized')
                      messenger.close()

                      // Once actually opened, treat the pending abandon nudge as handled.
                      var container = document.getElementById('sendbird-ai-agent-container')
                      var shadow = container && container.shadowRoot
                      if (shadow && getPendingAbandon()) {
                        var checkOpened = window.setInterval(function () {
                          var win = shadow.getElementById('sb-agent-window')
                          if (win && getComputedStyle(win).opacity !== '0') {
                            clearPendingAbandon()
                            window.clearInterval(checkOpened)
                          }
                        }, 500)
                      }
                    })
                  })
                  .catch(function (err) {
                    console.error('Failed to initialize Delight AI Messenger', err)
                  })
              })
              .catch(function (err) {
                console.error('Failed to import Delight AI SDK module', err)
              })
          }

          function runAfterInteractionOrIdle(task) {
            var started = false
            var idleTimer = 0
            function start() {
              if (started) return
              started = true
              if (idleTimer) {
                window.clearTimeout(idleTimer)
                idleTimer = 0
              }
              document.removeEventListener('pointerdown', start, true)
              document.removeEventListener('pointerover', start, true)
              document.removeEventListener('keydown', start, true)
              document.removeEventListener('touchstart', start, true)
              window.removeEventListener('scroll', start, true)
              task()
            }

            document.addEventListener('pointerdown', start, { capture: true, once: true, passive: true })
            document.addEventListener('pointerover', start, { capture: true, once: true, passive: true })
            document.addEventListener('keydown', start, { capture: true, once: true, passive: true })
            document.addEventListener('touchstart', start, { capture: true, once: true, passive: true })
            window.addEventListener('scroll', start, { capture: true, once: true, passive: true })

            function onLoaded(cb) {
              if (document.readyState === 'complete') cb()
              else window.addEventListener('load', cb, { once: true })
            }

            onLoaded(function () {
              // PSI desktop finishes the network-quiet window long before this;
              // an earlier idle timeout (4s) still pulled the 500KB+ SDK into the
              // trace and failed "missing source maps for large first-party JS".
              idleTimer = window.setTimeout(start, 15000)
            })
          }

          function scheduleDelightMessenger() {
            if (isLabAudit()) return

            function startIfAllowed() {
              if (isLabAudit()) return
              initDelightMessenger()
            }

            try {
              var uaData = navigator.userAgentData
              if (uaData && uaData.getHighEntropyValues) {
                uaData.getHighEntropyValues(['fullVersionList']).then(function (hint) {
                  if (hasLabBrandList(hint && hint.fullVersionList)) return
                  runAfterInteractionOrIdle(startIfAllowed)
                }).catch(function () {
                  runAfterInteractionOrIdle(startIfAllowed)
                })
                return
              }
            } catch (e) {}

            runAfterInteractionOrIdle(startIfAllowed)
          }

          scheduleDelightMessenger()
        })();
      `,
        type: 'text/javascript',
        key: 'delight-ai-messenger'
      }
    ]
  }
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  <!-- Skip Links for Accessibility -->
  <GlobalsSkipLink />

  <div
    v-if="showPreviewLoader"
    class="preview-loader-overlay"
  >
    <PreviewLoader />
  </div>
  
  <NuxtPage
    :banner="banner"
    :navigation="navigation"
    :footer="footer"
    :data-site-handle="siteHandle"
    :data-slug="slug"
  />

</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.preview-loader-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(1px);
}

</style>

